import apiClient from './apiClient';
import { ENDPOINTS } from './apiConfig';

/**
 * ------------------------------------------------------------------
 * SERVICIO DE PROVEEDORES (VENDORS)
 * ------------------------------------------------------------------
 * Maneja todas las operaciones CRUD para proveedores
 * Basado en la documentación:
 * POST   /api/vendor              -> Create or update
 * GET    /api/vendor              -> List vendors (requires empresa_rif)
 * GET    /api/vendor/{rif}        -> Get details (requires empresa_rif)
 * PUT    /api/vendor/{rif}        -> Update details
 * DELETE /api/vendor/{rif}        -> Delete (requires empresa_rif)
 */
/**
 * ------------------------------------------------------------------
 */

/**
 * Obtiene el RIF de la empresa
 * NOTA: Ahora debe pasarse como argumento desde el componente que usa el servicio
 */

/**
 * Transforma los datos del proveedor desde la API al formato del frontend
 * API usa: razon_social, correo, pagina_web, conceptos_idconceptos (snake_case)
 * Frontend usa: razonSocial, email, paginaWeb, concepto (camelCase)
 */
const transformFromApi = (apiData) => {
    if (!apiData) return null;

    return {
        ...apiData,
        id: apiData.id || apiData.idproveedor || apiData.rif, // Ensure ID for table selection
        // Conversiones snake_case → camelCase
        razonSocial: apiData.razon_social || apiData.razonSocial || '',
        paginaWeb: apiData.pagina_web || apiData.paginaWeb || '',
        email: apiData.correo || apiData.email || '',
        concepto: String(apiData.conceptos_idconceptos || apiData.concepto || ''),

        // Mantener ambos formatos por compatibilidad
        razon_social: apiData.razon_social || apiData.razonSocial || '',
        pagina_web: apiData.pagina_web || apiData.paginaWeb || '',
        correo: apiData.correo || apiData.email || '',
        conceptos_idconceptos: apiData.conceptos_idconceptos || apiData.concepto || ''
    };
};

/**
 * Transforma los datos del proveedor desde el frontend al formato de la API
 * Frontend usa: email, concepto
 * API espera: correo, conceptos_idconceptos
 */
const transformToApi = (frontendData) => {
    if (!frontendData) return null;

    console.log('[DEBUG] transformToApi input:', frontendData);

    const apiData = {
        empresa_rif: frontendData.empresa_rif,
        razon_social: frontendData.razonSocial || frontendData.razon_social || '',
        rif: frontendData.rif || '',
        direccion: frontendData.direccion || '',
        telefono: frontendData.telefono || '',
        contacto: frontendData.contacto || '',
        correo: frontendData.email || frontendData.correo || '',
        pagina_web: frontendData.paginaWeb || frontendData.pagina_web || '',
        retencion: parseFloat(frontendData.retencion) || 0,
        conceptos_idconceptos: parseInt(frontendData.concepto || frontendData.conceptos_idconceptos) || 1
    };

    console.log('[DEBUG] transformToApi output:', apiData);
    return apiData;
};

const proveedoresService = {
    /**
     * Obtener todos los proveedores de una empresa
     * @param {string} empresaRif - RIF de la empresa
     * @returns {Promise<Array>} Lista de proveedores
     */
    getAll: async (empresaRif) => {
        try {
            if (!empresaRif) throw new Error('empresaRif is required');
            const response = await apiClient.get(ENDPOINTS.PROVEEDORES, {
                params: { empresa_rif: empresaRif }
            });
            const data = response.data || response;
            // Transformar cada proveedor del formato API al formato frontend
            return Array.isArray(data) ? data.map(transformFromApi) : [];
        } catch (error) {
            console.error('Error fetching proveedores:', error);
            throw error;
        }
    },

    /**
     * Obtener un proveedor por RIF
     * @param {string} rif - RIF del proveedor
     * @param {string} empresaRif - RIF de la empresa
     * @returns {Promise<Object>} Datos del proveedor
     */
    getByRif: async (rif, empresaRif) => {
        try {
            if (!empresaRif) throw new Error('empresaRif is required');
            const response = await apiClient.get(ENDPOINTS.PROVEEDOR_BY_RIF(rif), {
                params: { empresa_rif: empresaRif }
            });
            const data = response.data || response;
            return transformFromApi(data);
        } catch (error) {
            console.error(`Error fetching proveedor ${rif}:`, error);
            throw error;
        }
    },

    /**
     * Crear o actualizar un proveedor
     * @param {Object} proveedor - Datos del proveedor
     * @returns {Promise<Object>} Proveedor creado/actualizado
     */
    create: async (proveedor) => {
        try {
            // Transformar datos del frontend al formato API
            const payload = transformToApi(proveedor);
            const response = await apiClient.post(ENDPOINTS.PROVEEDORES, payload);
            const data = response.data || response;
            return transformFromApi(data);
        } catch (error) {
            console.error('Error creating proveedor:', error);
            throw error;
        }
    },

    /**
     * Actualizar un proveedor existente
     * @param {string} rif - RIF del proveedor (usado como ID)
     * @param {Object} proveedor - Datos actualizados
     * @returns {Promise<Object>} Proveedor actualizado
     */
    update: async (rif, proveedor) => {
        try {
            // Transformar datos del frontend al formato API
            const payload = transformToApi(proveedor);
            const response = await apiClient.put(ENDPOINTS.PROVEEDOR_BY_RIF(rif), payload);
            const data = response.data || response;
            return transformFromApi(data);
        } catch (error) {
            console.error(`Error updating proveedor ${rif}:`, error);
            throw error;
        }
    },

    /**
     * Eliminar un proveedor
     * @param {string} rif - RIF del proveedor
     * @param {string} empresaRif - RIF de la empresa
     * @returns {Promise<void>}
     */
    delete: async (rif, empresaRif) => {
        try {
            if (!empresaRif) throw new Error('empresaRif is required');
            await apiClient.delete(ENDPOINTS.PROVEEDOR_BY_RIF(rif), {
                params: { empresa_rif: empresaRif }
            });
        } catch (error) {
            console.error(`Error deleting proveedor ${rif}:`, error);
            throw error;
        }
    }
};

export default proveedoresService;
